import React, { useMemo } from 'react';

import { Link } from '~/lib/react-router-dom';

import { Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { GetRateCodes } from '~/services/cruises/RateCodeService';

type RateCodeRow = {
  id: number;
  rateId: string;
  rateCode: string;
  description: string;
  isEnabled: boolean;
  isNetRate: boolean;
  isFlash: boolean;
  vendorCode: string;
  rateGroup: string;
};

const urlBuilder = (id: string): string => `/cruises/rate-code-management/${id}/edit`;

const columns: Array<GridColDef> = [
  {
    width: 100,
    editable: true,
    field: 'vendorCode',
    headerName: 'Vendor',
    display: 'flex',
  },
  {
    width: 200,
    editable: true,
    field: 'rateCode',
    headerName: 'Rate Code',
    display: 'flex',
    renderCell: (params: GridRenderCellParams<RateCodeRow>) => (
      <Typography variant="inherit" noWrap>
        <Link to={urlBuilder(params.row.rateId)}>
          <Typography variant="inherit" noWrap>
            {params.row.rateCode}
          </Typography>
        </Link>
      </Typography>
    ),
  },
  {
    width: 200,
    editable: true,
    field: 'rateCodeDescription',
    headerName: 'Rate Code Description',
    display: 'flex',
    renderCell: (params: GridRenderCellParams<RateCodeRow>) => (
      <Typography variant="inherit" noWrap>
        {params.row.description || '-'}
      </Typography>
    ),
  },
  {
    width: 100,
    editable: false,
    field: 'isEnabled',
    headerName: 'Is Enabled?',
    display: 'flex',
    renderCell: (params: GridRenderCellParams<RateCodeRow>) => (
      <Typography variant="inherit" noWrap>
        {params.row.isEnabled ? 'YES' : 'NO'}
      </Typography>
    ),
  },
  {
    width: 100,
    editable: false,
    field: 'isNetRate',
    headerName: 'Is Net Rate?',
    display: 'flex',
    renderCell: (params: GridRenderCellParams<RateCodeRow>) => (
      <Typography variant="inherit" noWrap>
        {params.row.isNetRate ? 'YES' : 'NO'}
      </Typography>
    ),
  },
  {
    width: 100,
    editable: false,
    field: 'isFlash',
    headerName: 'Is Flash?',
    display: 'flex',
    renderCell: (params: GridRenderCellParams<RateCodeRow>) => (
      <Typography variant="inherit" noWrap>
        {params.row.isFlash ? 'YES' : 'NO'}
      </Typography>
    ),
  },
  {
    width: 100,
    editable: false,
    field: 'rateGroup',
    headerName: 'Rate Group',
    renderCell: (params: GridRenderCellParams<RateCodeRow>) => (
      <Typography variant="inherit" noWrap>
        {params.row.rateGroup || '-'}
      </Typography>
    ),
  },
];

const dataGridInitialState = {
  pagination: { paginationModel: { pageSize: 10 } },
};

type Props = {
  page: number;
  total: number;
  perPage: number;
  loading: boolean;
  onPaginate: (page: number) => void;
  rateCodes: Array<GetRateCodes>;
};

function RateCodeList({ loading, rateCodes, page, perPage, total, onPaginate }: Props) {
  const dataRows: Array<RateCodeRow> = useMemo(() => {
    return rateCodes.map((rate, id) => {
      const refundableLabel = rate.rateGroup?.isRefundable ? 'Refundable' : 'Non-Refundable';

      return {
        id,
        rateId: rate.id,
        rateCode: rate.code,
        description: rate.description,
        isEnabled: rate.isEnabled,
        isNetRate: rate.isNetRate,
        isFlash: rate.isFlash,
        vendorCode: rate.vendor.code,
        rateGroup: rate.rateGroup ? `${rate.rateGroup?.name} (${refundableLabel})` : '-',
      };
    });
  }, [rateCodes]);

  return (
    <DataGrid
      rows={dataRows}
      columns={columns}
      loading={loading}
      pagination
      rowCount={total}
      getRowId={(row) => row.id}
      paginationMode="server"
      paginationModel={{ page: page, pageSize: perPage }}
      onPaginationModelChange={({ page }) => onPaginate(page)}
      slots={{ pagination: GridPagination }}
      pageSizeOptions={[10]}
      initialState={dataGridInitialState}
      autoHeight
    />
  );
}

export default RateCodeList;
